/**
 * Cached wrapper for Aurora API with configurable expiry times
 * Stations: 3 minutes cache, Polar Oval: 3 minutes cache
 *
 * Stations data bundles per-station magdata (updated frequently) alongside
 * mostly-static station metadata, so it's cached on the same short interval
 * as the other dynamic datasets. If the endpoint is ever split into separate
 * static/dynamic responses, the static half could move to a much longer
 * (e.g. 1 hour) cache duration.
 */

import { AuroraApi, type Station, type PolarOvalPoint, type OvationAuroraCoordinate, type PolarElectroJetPoint } from './aurora-api';
import { cacheService } from './cache-service';



interface CacheConfig {
  stations: {
    key: string;
    duration: number;
  };
  polarOval: {
    key: string;
    duration: number;
  };
  ovationAurora: {
    key: string;
    duration: number;
  };
  polarElectroJet: {
    key: string;
    duration: number;
  };
}

export class AuroraCachedApi {
  private static readonly CACHE_CONFIG: CacheConfig = {
    stations: {
      key: 'aurora_stations',
      duration: 3 * 60 * 1000, // 3 minutes in milliseconds
    },
    polarOval: {
      key: 'aurora_polar_oval',
      duration: 3 * 60 * 1000, // 3 minutes in milliseconds
    },
    ovationAurora: {
      key: 'aurora_ovation',
      duration: 3 * 60 * 1000, // 3 minutes in milliseconds
    },
    polarElectroJet: {
      key: 'aurora_polar_electro_jet',
      duration: 3 * 60 * 1000, // 3 minutes in milliseconds
    },
  };

  /**
   * Get stations data with 3-minutes caching
   * Returns cached data if available and not expired, otherwise fetches fresh data
   */
  static async getStations(): Promise<Station[]> {
    const config = this.CACHE_CONFIG.stations;

    try {
      // Try to get from cache first
      const cachedData = await cacheService.get<Station[]>(config.key);
      if (cachedData) {
        console.log('✅ Stations loaded from cache');
        return cachedData;
      }

      // Cache miss or expired - fetch fresh data
      console.log('🌐 Fetching fresh stations data...');
      const freshData = await AuroraApi.getStations();

      // Cache the fresh data
      if (freshData && freshData.length > 0) {
        await cacheService.set(config.key, freshData, config.duration);
        console.log('💾 Stations data cached for 3 minutes');
      }

      return freshData;
    } catch (error) {
      console.error('Error in cached getStations:', error);

      // Try to return stale cached data as fallback
      try {
        const staleData = await cacheService.get<Station[]>(config.key);
        if (staleData) {
          console.warn('⚠️ Returning stale stations data due to error');
          return staleData;
        }
      } catch (cacheError) {
        console.error('Failed to get stale cache data:', cacheError);
      }

      // Last resort: return empty array
      return [];
    }
  }

  /**
   * Get polar oval data with 3-minute caching
   * Returns cached data if available and not expired, otherwise fetches fresh data
   */
  static async getPolarOval(): Promise<PolarOvalPoint[]> {
    const config = this.CACHE_CONFIG.polarOval;

    try {
      // Try to get from cache first
      const cachedData = await cacheService.get<PolarOvalPoint[]>(config.key);
      if (cachedData) {
        console.log('✅ Polar oval loaded from cache');
        return cachedData;
      }

      // Cache miss or expired - fetch fresh data
      console.log('🌐 Fetching fresh polar oval data...');
      const freshData = await AuroraApi.getPolarOval();

      // Cache the fresh data
      if (freshData && freshData.length > 0) {
        await cacheService.set(config.key, freshData, config.duration);
        console.log('💾 Polar oval data cached for 3 minutes');
      }

      return freshData;
    } catch (error) {
      console.error('Error in cached getPolarOval:', error);

      // Try to return stale cached data as fallback
      try {
        const staleData = await cacheService.get<PolarOvalPoint[]>(config.key);
        if (staleData) {
          console.warn('⚠️ Returning stale polar oval data due to error');
          return staleData;
        }
      } catch (cacheError) {
        console.error('Failed to get stale cache data:', cacheError);
      }

      // Last resort: return empty array
      return [];
    }
  }

  /**
   * Get NOAA Ovation Aurora data with 3-minute caching
   * Returns cached data if available and not expired, otherwise fetches fresh data
   */
  static async getOvationAurora(): Promise<OvationAuroraCoordinate[]> {
    const config = this.CACHE_CONFIG.ovationAurora;

    try {
      // Try to get from cache first
      const cachedData = await cacheService.get<OvationAuroraCoordinate[]>(config.key);
      if (cachedData) {
        console.log('✅ Ovation Aurora loaded from cache');
        return cachedData;
      }

      // Cache miss or expired - fetch fresh data
      console.log('🌐 Fetching fresh Ovation Aurora data...');
      const freshData = await AuroraApi.getOvationAurora();

      // Cache the fresh data
      if (freshData && freshData.length > 0) {
        await cacheService.set(config.key, freshData, config.duration);
        console.log('💾 Ovation Aurora data cached for 3 minutes');
      }

      return freshData;
    } catch (error) {
      console.error('Error in cached getOvationAurora:', error);

      // Try to return stale cached data as fallback
      try {
        const staleData = await cacheService.get<OvationAuroraCoordinate[]>(config.key);
        if (staleData) {
          console.warn('⚠️ Returning stale Ovation Aurora data due to error');
          return staleData;
        }
      } catch (cacheError) {
        console.error('Failed to get stale cache data:', cacheError);
      }

      // Last resort: return empty array
      return [];
    }
  }

  /**
   * Get Polar Electro Jet data with 3-minute caching
   * Returns cached data if available and not expired, otherwise fetches fresh data
   */
  static async getPolarElectroJet(): Promise<PolarElectroJetPoint[]> {
    const config = this.CACHE_CONFIG.polarElectroJet;

    try {
      // Try to get from cache first
      const cachedData = await cacheService.get<PolarElectroJetPoint[]>(config.key);
      if (cachedData) {
        console.log('✅ Polar Electro Jet loaded from cache');
        return cachedData;
      }

      // Cache miss or expired - fetch fresh data
      console.log('🌐 Fetching fresh Polar Electro Jet data...');
      const freshData = await AuroraApi.getPolarElectroJet();

      // Cache the fresh data
      if (freshData && freshData.length > 0) {
        await cacheService.set(config.key, freshData, config.duration);
        console.log('💾 Polar Electro Jet data cached for 3 minutes');
      }

      return freshData;
    } catch (error) {
      console.error('Error in cached getPolarElectroJet:', error);

      // Try to return stale cached data as fallback
      try {
        const staleData = await cacheService.get<PolarElectroJetPoint[]>(config.key);
        if (staleData) {
          console.warn('⚠️ Returning stale Polar Electro Jet data due to error');
          return staleData;
        }
      } catch (cacheError) {
        console.error('Failed to get stale cache data:', cacheError);
      }

      // Last resort: return empty array
      return [];
    }
  }

  /**
   * Manually invalidate stations cache
   * Useful for forcing a refresh
   */
  static async invalidateStationsCache(): Promise<void> {
    await cacheService.invalidate(this.CACHE_CONFIG.stations.key);
    console.log('🗑️ Stations cache invalidated');
  }

  /**
   * Manually invalidate polar oval cache
   * Useful for forcing a refresh
   */
  static async invalidatePolarOvalCache(): Promise<void> {
    await cacheService.invalidate(this.CACHE_CONFIG.polarOval.key);
    console.log('🗑️ Polar oval cache invalidated');
  }

  /**
   * Manually invalidate Ovation Aurora cache
   * Useful for forcing a refresh
   */
  static async invalidateOvationAuroraCache(): Promise<void> {
    await cacheService.invalidate(this.CACHE_CONFIG.ovationAurora.key);
    console.log('🗑️ Ovation Aurora cache invalidated');
  }

  /**
   * Manually invalidate Polar Electro Jet cache
   * Useful for forcing a refresh
   */
  static async invalidatePolarElectroJetCache(): Promise<void> {
    await cacheService.invalidate(this.CACHE_CONFIG.polarElectroJet.key);
    console.log('🗑️ Polar Electro Jet cache invalidated');
  }

  /**
   * Clear all Aurora API cache
   */
  static async clearAllCache(): Promise<void> {
    await this.invalidateStationsCache();
    await this.invalidatePolarOvalCache();
    await this.invalidateOvationAuroraCache();
    await this.invalidatePolarElectroJetCache();
    console.log('🗑️ All Aurora API cache cleared');
  }

  /**
   * Get cache status and statistics
   */
  static async getCacheStatus(): Promise<{
    stations: { cached: boolean; age?: number };
    polarOval: { cached: boolean; age?: number };
    ovationAurora: { cached: boolean; age?: number };
    polarElectroJet: { cached: boolean; age?: number };
    cacheStats: { memoryEntries: number; localStorageEntries: number; estimatedSize: number };
  }> {
    const stationsConfig = this.CACHE_CONFIG.stations;
    const polarOvalConfig = this.CACHE_CONFIG.polarOval;
    const ovationAuroraConfig = this.CACHE_CONFIG.ovationAurora;
    const polarElectroJetConfig = this.CACHE_CONFIG.polarElectroJet;

    const stationsCached = await cacheService.has(stationsConfig.key);
    const polarOvalCached = await cacheService.has(polarOvalConfig.key);
    const ovationAuroraCached = await cacheService.has(ovationAuroraConfig.key);
    const polarElectroJetCached = await cacheService.has(polarElectroJetConfig.key);

    const cacheStats = cacheService.getStats();

    return {
      stations: {
        cached: stationsCached,
        age: stationsCached ? await this.getCacheAge(stationsConfig.key) : undefined
      },
      polarOval: {
        cached: polarOvalCached,
        age: polarOvalCached ? await this.getCacheAge(polarOvalConfig.key) : undefined
      },
      ovationAurora: {
        cached: ovationAuroraCached,
        age: ovationAuroraCached ? await this.getCacheAge(ovationAuroraConfig.key) : undefined
      },
      polarElectroJet: {
        cached: polarElectroJetCached,
        age: polarElectroJetCached ? await this.getCacheAge(polarElectroJetConfig.key) : undefined
      },
      cacheStats
    };
  }

  /**
   * Get the age of cached data in milliseconds
   */
  private static async getCacheAge(key: string): Promise<number | undefined> {
    try {
      // This is a bit of a hack to get the timestamp without exposing it in the cache service
      // We'll get the data and check if it exists, then calculate age
      const data = await cacheService.get(key);
      if (data) {
        // Since we can't directly access timestamp, we'll estimate based on localStorage
        const storageKey = 'aurora_cache_' + key;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const entry = JSON.parse(stored);
          return Date.now() - entry.timestamp;
        }
      }
    } catch (error) {
      console.warn('Failed to get cache age:', error);
    }
    return undefined;
  }

  /**
   * Preload data into cache
   * Useful for warming up the cache
   */
  static async preloadCache(): Promise<void> {
    console.log('🔄 Preloading Aurora API cache...');

    try {
      // Preload all datasets in parallel
      const [stations, polarOval, ovationAurora, polarElectroJet] = await Promise.all([
        this.getStations(),
        this.getPolarOval(),
        this.getOvationAurora(),
        this.getPolarElectroJet()
      ]);

      console.log(`✅ Cache preloaded: ${stations.length} stations, ${polarOval.length} polar oval points, ${ovationAurora.length} ovation points, ${polarElectroJet.length} polar electro jet points`);
    } catch (error) {
      console.error('Failed to preload cache:', error);
    }
  }

  /**
   * Background refresh - fetch fresh data without waiting
   * Updates cache in the background while returning current cached data
   */
  static async backgroundRefresh(dataType: 'stations' | 'polarOval' | 'ovationAurora' | 'polarElectroJet' | 'all' = 'all'): Promise<void> {
    console.log('🔄 Starting background refresh...');

    const refreshPromises: Promise<any>[] = [];

    if (dataType === 'stations' || dataType === 'all') {
      refreshPromises.push(
        AuroraApi.getStations().then(data => {
          if (data && data.length > 0) {
            return cacheService.set(this.CACHE_CONFIG.stations.key, data, this.CACHE_CONFIG.stations.duration);
          }
          return null;
        }).catch(error => console.warn('Background refresh failed for stations:', error))
      );
    }

    if (dataType === 'polarOval' || dataType === 'all') {
      refreshPromises.push(
        AuroraApi.getPolarOval().then(data => {
          if (data && data.length > 0) {
            return cacheService.set(this.CACHE_CONFIG.polarOval.key, data, this.CACHE_CONFIG.polarOval.duration);
          }
          return null;
        }).catch(error => console.warn('Background refresh failed for polar oval:', error))
      );
    }

    if (dataType === 'ovationAurora' || dataType === 'all') {
      refreshPromises.push(
        AuroraApi.getOvationAurora().then(data => {
          if (data && data.length > 0) {
            return cacheService.set(this.CACHE_CONFIG.ovationAurora.key, data, this.CACHE_CONFIG.ovationAurora.duration);
          }
          return null;
        }).catch(error => console.warn('Background refresh failed for ovation aurora:', error))
      );
    }

    if (dataType === 'polarElectroJet' || dataType === 'all') {
      refreshPromises.push(
        AuroraApi.getPolarElectroJet().then(data => {
          if (data && data.length > 0) {
            return cacheService.set(this.CACHE_CONFIG.polarElectroJet.key, data, this.CACHE_CONFIG.polarElectroJet.duration);
          }
          return null;
        }).catch(error => console.warn('Background refresh failed for polar electro jet:', error))
      );
    }

    // Don't await - let it run in background
    Promise.all(refreshPromises).then(() => {
      console.log('✅ Background refresh completed');
    });
  }
}